import { trackEvent } from "@/lib/analytics";
import { getEabBanner, MEMBERSHIP_BANNER, type EabBanner } from "@/content/eab-banners";
import eabLogo from "@/assets/eab/employment-law-advice-bureau-logo.png.asset.json";

interface EabBannerProps {
  /** Resolve the banner from a route path. */
  path?: string;
  /** Or pass a banner directly. */
  banner?: EabBanner;
  /** Force the free membership banner. */
  membership?: boolean;
  /** Where the banner was rendered (analytics). */
  source?: string;
  /** `card` (default) is the compact inline module; `wide` keeps the legacy full-width image. */
  variant?: "card" | "wide";
  className?: string;
}

export function EabBannerBlock({
  path,
  banner,
  membership,
  source = "page",
  variant = "card",
  className = "",
}: EabBannerProps) {
  const b = banner ?? (membership ? MEMBERSHIP_BANNER : getEabBanner(path ?? "/"));

  const onClick = () =>
    trackEvent("eab_banner_click", {
      category: "partner",
      action: source,
      label: b.id,
      properties: { destination: b.targetUrl, file: b.fileName },
    });

  if (variant === "wide") {
    return (
      <section className={`px-6 pb-16 lg:px-8 ${className}`}>
        <div className="mx-auto max-w-5xl">
          <a
            href={b.targetUrl}
            target="_blank"
            rel="sponsored noopener noreferrer"
            title={b.title}
            onClick={onClick}
            className="group block overflow-hidden rounded-2xl ring-1 ring-black/5 transition hover:ring-accent/40"
          >
            <img
              src={b.src}
              alt={b.alt}
              title={b.title}
              width={1536}
              height={512}
              loading="lazy"
              decoding="async"
              className="h-auto w-full"
            />
          </a>
          <p className="mt-3 text-xs leading-relaxed text-muted-foreground">{b.caption}</p>
        </div>
      </section>
    );
  }

  return (
    <section className={`px-6 pb-10 lg:px-8 ${className}`}>
      <div className="mx-auto max-w-3xl">
        <a
          href={b.targetUrl}
          target="_blank"
          rel="sponsored noopener noreferrer"
          title={b.title}
          onClick={onClick}
          className="group relative flex flex-col gap-3 rounded-xl border border-border bg-card p-5 transition hover:border-accent/50 hover:shadow-sm"
        >
          <div className="flex items-start justify-between gap-4">
            <span className="inline-flex w-fit items-center rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
              Partner
            </span>
            <img
              src={eabLogo.url}
              alt="The Employment Law Advice Bureau"
              title={b.title}
              width={600}
              height={140}
              loading="lazy"
              decoding="async"
              className="h-8 w-auto opacity-90 transition-opacity group-hover:opacity-100"
            />
          </div>
          <div className="flex min-w-0 flex-col gap-1.5">
            <p className="text-sm font-semibold leading-snug text-foreground">{b.title}</p>
            <p className="text-xs leading-relaxed text-muted-foreground">{b.caption}</p>
            <span className="mt-0.5 text-xs font-medium text-accent group-hover:underline">
              Visit The Employment Law Advice Bureau →
            </span>
          </div>
        </a>
        <p className="mt-2 text-[11px] leading-relaxed text-muted-foreground/70">
          We may be compensated if you use this service. It stays free for employers.
        </p>
      </div>
    </section>
  );
}
